import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';
import { Icons } from '../../../components';
import { normalizeAccessFilter } from '../../../util/publicMoorages';
import {
  ACCESS_FILTER_ALL,
  ACCESS_FILTER_PRIVATE,
  ACCESS_FILTER_PUBLIC,
} from '../../../util/publicMoorageConstants';

import FilterPopup from '../FilterPopup/FilterPopup';

import css from './AccessFilter.module.css';

const AccessFilterOption = props => {
  const { id, name, value, label } = props;

  return (
    <li className={css.optionItem}>
      <Field name={name} type="radio" value={value}>
        {fieldRenderProps => {
          const { input } = fieldRenderProps;

          return (
            <label htmlFor={id} className={css.optionLabel}>
              <input id={id} className={css.optionInput} {...input} />
              <span
                className={classNames(css.optionCheckbox, {
                  [css.optionCheckboxChecked]: input.checked,
                })}
                aria-hidden="true"
              />
              <span className={css.optionText}>{label}</span>
            </label>
          );
        }}
      </Field>
    </li>
  );
};

const AccessFilter = props => {
  const intl = useIntl();
  const {
    value = ACCESS_FILTER_ALL,
    className,
    rootClassName,
    onChange = () => {},
    variant = 'default',
    id = 'SearchPage.accessFilter',
    contentPlacementOffset = 0,
  } = props;

  const isMobilePillsVariant = variant === 'mobilePills';
  const normalizedValue = normalizeAccessFilter(value);
  const classes = classNames(rootClassName || css.root, className, {
    [css.mobileRoot]: isMobilePillsVariant,
  });
  const allOptions = [
    {
      value: ACCESS_FILTER_ALL,
      label: intl.formatMessage({ id: 'SearchPage.accessAll', defaultMessage: 'All' }),
      className: css.allPill,
    },
    {
      value: ACCESS_FILTER_PUBLIC,
      label: intl.formatMessage({ id: 'SearchPage.accessPublic', defaultMessage: 'Public' }),
      className: css.publicPill,
      iconName: 'anchor',
    },
    {
      value: ACCESS_FILTER_PRIVATE,
      label: intl.formatMessage({ id: 'SearchPage.accessPrivate', defaultMessage: 'Private' }),
      className: css.privatePill,
      iconName: 'users',
    },
  ];
  const options = isMobilePillsVariant
    ? allOptions.filter(option => option.value !== ACCESS_FILTER_ALL)
    : allOptions;

  const mobilePills = (
    <div className={classes}>
      {isMobilePillsVariant ? null : (
        <span className={css.label}>
          {intl.formatMessage({ id: 'SearchPage.accessFilter', defaultMessage: 'Access' })}
        </span>
      )}
      <div className={css.pills} role="group" aria-label="Access">
        {options.map(option => {
          const isSelected = isMobilePillsVariant
            ? normalizedValue === ACCESS_FILTER_ALL || option.value === normalizedValue
            : option.value === normalizedValue;
          const nextValue =
            isMobilePillsVariant && normalizedValue === option.value
              ? ACCESS_FILTER_ALL
              : option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={classNames(css.pill, option.className, {
                [css.pillSelected]: isSelected,
                [css.mobilePill]: isMobilePillsVariant,
              })}
              aria-pressed={isSelected}
              onClick={() => onChange(nextValue)}
            >
              {isMobilePillsVariant && option.iconName ? (
                <span className={css.pillIcon} aria-hidden="true">
                  <Icons name={option.iconName} />
                </span>
              ) : null}
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  if (isMobilePillsVariant) {
    return mobilePills;
  }

  const popupLabel = intl.formatMessage({
    id: 'SearchPage.accessFilter',
    defaultMessage: 'Access',
  });
  const handleSubmit = values => {
    onChange(normalizeAccessFilter(values?.access));
  };

  return (
    <FilterPopup
      className={classNames(rootClassName || css.desktopRoot, className)}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={popupLabel}
      isSelected={normalizedValue !== ACCESS_FILTER_ALL}
      id={`${id}.popup`}
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={{ access: normalizedValue }}
      keepDirtyOnReinitialize
    >
      <fieldset className={css.fieldGroup}>
        <ul className={css.optionList}>
          {allOptions.map(option => (
            <AccessFilterOption
              key={option.value}
              id={`${id}.${option.value}`}
              name="access"
              value={option.value}
              label={option.label}
            />
          ))}
        </ul>
      </fieldset>
    </FilterPopup>
  );
};

export default AccessFilter;
